export type FormulaValue = number | string | boolean | null;
type Value = FormulaValue | FormulaValue[] | FormulaError;

class FormulaError {
  constructor(public code: string) {}
}

interface RefNode {
  type: 'ref';
  row: number;
  col: number;
  absRow: boolean;
  absCol: boolean;
}

type Node =
  | { type: 'num'; value: number }
  | { type: 'str'; value: string }
  | { type: 'bool'; value: boolean }
  | RefNode
  | { type: 'range'; from: RefNode; to: RefNode }
  | { type: 'unary'; op: string; arg: Node }
  | { type: 'postfix'; op: string; arg: Node }
  | { type: 'binary'; op: string; left: Node; right: Node }
  | { type: 'call'; name: string; args: Node[] };

interface Token {
  kind: 'num' | 'str' | 'ref' | 'name' | 'op' | 'lparen' | 'rparen' | 'sep';
  text: string;
}

function columnNumber(letters: string): number {
  let index = 0;
  for (const letter of letters.toUpperCase()) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const char = source[i];
    if (/\s/.test(char)) {
      i++;
      continue;
    }
    if (char === '"') {
      let text = '';
      i++;
      while (i < source.length) {
        if (source[i] === '"') {
          if (source[i + 1] === '"') {
            text += '"';
            i += 2;
            continue;
          }
          break;
        }
        text += source[i++];
      }
      i++;
      tokens.push({ kind: 'str', text });
      continue;
    }
    if (char === "'") {
      const end = source.indexOf("'!", i + 1);
      i = end < 0 ? source.length : end + 2;
      continue;
    }
    const number = source.slice(i).match(/^\d+(\.\d+)?([eE][+-]?\d+)?/);
    if (number) {
      tokens.push({ kind: 'num', text: number[0] });
      i += number[0].length;
      continue;
    }
    const word = source.slice(i).match(/^[A-Za-z_Ѐ-ӿ$][A-Za-z0-9_.Ѐ-ӿ$]*/);
    if (word) {
      if (source[i + word[0].length] === '!') {
        i += word[0].length + 1;
        continue;
      }
      const isRef = /^\$?[A-Za-z]{1,3}\$?\d+$/.test(word[0]) && source[i + word[0].length] !== '(';
      tokens.push({ kind: isRef ? 'ref' : 'name', text: word[0] });
      i += word[0].length;
      continue;
    }
    const twoChar = source.slice(i, i + 2);
    if (twoChar === '<=' || twoChar === '>=' || twoChar === '<>') {
      tokens.push({ kind: 'op', text: twoChar });
      i += 2;
      continue;
    }
    if (char === '(') {
      tokens.push({ kind: 'lparen', text: char });
    } else if (char === ')') {
      tokens.push({ kind: 'rparen', text: char });
    } else if (char === ',' || char === ';') {
      tokens.push({ kind: 'sep', text: char });
    } else {
      tokens.push({ kind: 'op', text: char });
    }
    i++;
  }
  return tokens;
}

function parseRef(text: string): RefNode {
  const match = text.match(/^(\$?)([A-Za-z]{1,3})(\$?)(\d+)$/);
  return {
    type: 'ref',
    absCol: match[1] === '$',
    col: columnNumber(match[2]),
    absRow: match[3] === '$',
    row: Number(match[4]) - 1,
  };
}

const BINARY_PRECEDENCE: Record<string, number> = {
  '=': 1,
  '<>': 1,
  '<': 1,
  '>': 1,
  '<=': 1,
  '>=': 1,
  '&': 2,
  '+': 3,
  '-': 3,
  '*': 4,
  '/': 4,
  '^': 5,
};

function parse(tokens: Token[]): Node {
  let position = 0;
  const peek = () => tokens[position];
  const next = () => tokens[position++];

  function primary(): Node {
    const token = next();
    if (!token) {
      throw new Error('unexpected end');
    }
    if (token.kind === 'num') {
      return { type: 'num', value: Number(token.text) };
    }
    if (token.kind === 'str') {
      return { type: 'str', value: token.text };
    }
    if (token.kind === 'op' && (token.text === '-' || token.text === '+')) {
      return { type: 'unary', op: token.text, arg: expression(6) };
    }

    if (token.kind === 'lparen') {
      const inner = expression(0);
      next();
      return inner;
    }
    if (token.kind === 'ref') {
      const from = parseRef(token.text);
      if (peek()?.text === ':' && tokens[position + 1]?.kind === 'ref') {
        next();
        return { type: 'range', from, to: parseRef(next().text) };
      }
      return from;
    }
    if (token.kind === 'name') {
      const upper = token.text.toUpperCase().replace(/^_XLFN\./, '');
      if (peek()?.kind === 'lparen') {
        next();
        const args: Node[] = [];
        if (peek()?.kind !== 'rparen') {
          for (;;) {
            args.push(expression(0));
            if (peek()?.kind === 'sep') {
              next();
            } else {
              break;
            }
          }
        }
        next();
        return { type: 'call', name: upper, args };
      }
      if (upper === 'TRUE') {
        return { type: 'bool', value: true };
      }
      if (upper === 'FALSE') {
        return { type: 'bool', value: false };
      }
    }
    throw new Error(`unexpected ${token.text}`);
  }

  function expression(minPrecedence: number): Node {
    let left = primary();
    for (;;) {
      const token = peek();
      if (token?.kind !== 'op') {
        break;
      }
      if (token.text === '%') {
        next();
        left = { type: 'postfix', op: '%', arg: left };
        continue;
      }
      const precedence = BINARY_PRECEDENCE[token.text];
      if (precedence === undefined || precedence < minPrecedence) {
        break;
      }
      next();
      const right = expression(token.text === '^' ? precedence : precedence + 1);
      left = { type: 'binary', op: token.text, left, right };
    }
    return left;
  }

  const result = expression(0);
  if (position < tokens.length) {
    throw new Error('trailing tokens');
  }
  return result;
}

const cache = new Map<string, Node | null>();

export function compileFormula(source: string): Node | null {
  if (cache.has(source)) {
    return cache.get(source);
  }
  let node: Node | null = null;
  try {
    node = parse(tokenize(source.replace(/^=/, '')));
  } catch {
    node = null;
  }
  cache.set(source, node);
  return node;
}

export interface FormulaContext {
  row: number;
  col: number;
  originRow: number;
  originCol: number;
  getValue: (row: number, col: number) => FormulaValue;
  date1904?: boolean;
  rowCount: number;
  columnCount: number;
  budget: { reads: number };
}

function resolve(ref: RefNode, context: FormulaContext): [number, number] {
  const row = ref.absRow ? ref.row : ref.row + context.row - context.originRow;
  const col = ref.absCol ? ref.col : ref.col + context.col - context.originCol;
  return [row, col];
}

function toNumber(value: Value): number | FormulaError {
  if (value instanceof FormulaError) {
    return value;
  }
  if (Array.isArray(value)) {
    return toNumber(value[0] ?? null);
  }
  if (value === null || value === '') {
    return 0;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value.replace(',', '.'));
  return Number.isNaN(parsed) ? new FormulaError('#VALUE!') : parsed;
}

function toText(value: Value): string {
  if (Array.isArray(value)) {
    return toText(value[0] ?? null);
  }
  if (value instanceof FormulaError) {
    return value.code;
  }
  if (value === null) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  return String(value);
}

function toBool(value: Value): boolean {
  if (Array.isArray(value)) {
    return toBool(value[0] ?? null);
  }
  if (value instanceof FormulaError || value === null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.toUpperCase() === 'TRUE';
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return value;
}

function scalar(value: Value): FormulaValue | FormulaError {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value;
}

function compare(left: FormulaValue, right: FormulaValue): number {
  const rank = (value: FormulaValue) => (typeof value === 'boolean' ? 2 : typeof value === 'string' ? 1 : 0);
  const a = left ?? (typeof right === 'string' ? '' : 0);
  const b = right ?? (typeof left === 'string' ? '' : 0);
  if (rank(a) !== rank(b)) {
    return rank(a) - rank(b);
  }
  if (typeof a === 'string') {
    return a.toLowerCase().localeCompare((b as string).toLowerCase());
  }
  return Number(a) - Number(b);
}

function flatten(values: Value[]): FormulaValue[] {
  return values.flatMap((value) => (Array.isArray(value) ? value : [value instanceof FormulaError ? null : value]));
}

function matchesCriteria(value: FormulaValue, criteria: FormulaValue): boolean {
  if (typeof criteria === 'string') {
    if (criteria.length > 255) {
      return false;
    }
    const match = criteria.match(/^(<=|>=|<>|<|>|=)?(.*)$/);
    const op = match[1] ?? '=';
    const operand: FormulaValue = match[2] !== '' && !Number.isNaN(Number(match[2])) ? Number(match[2]) : match[2];
    if (typeof operand === 'string' && /[*?]/.test(operand) && (op === '=' || op === '<>')) {
      const pattern = new RegExp(
        `^${operand
          .replace(/[.+^${}()|[\]\\]/g, '\\$&')
          .replace(/\*/g, '.*')
          .replace(/\?/g, '.')}$`,
        'i',
      );
      const found = pattern.test(toText(value));
      return op === '=' ? found : !found;
    }
    if (typeof operand === 'number' && typeof value !== 'number') {
      return op === '<>';
    }
    const result = compare(value, operand);
    switch (op) {
      case '<':
        return result < 0;
      case '<=':
        return result <= 0;
      case '>':
        return result > 0;
      case '>=':
        return result >= 0;
      case '<>':
        return result !== 0;
      default:
        return result === 0;
    }
  }
  return compare(value, criteria) === 0;
}

function serialToday(date1904: boolean): number {
  const now = new Date();
  const utc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const serial = (utc - Date.UTC(1899, 11, 30)) / 86400000;
  return date1904 ? serial - 1462 : serial;
}

function serialToDate(serial: number, date1904: boolean): Date {
  const days = date1904 ? serial + 1462 : serial;
  return new Date(Date.UTC(1899, 11, 30) + Math.floor(days) * 86400000);
}

function evaluate(node: Node, context: FormulaContext): Value {
  switch (node.type) {
    case 'num':
    case 'str':
    case 'bool':
      return node.value;
    case 'ref': {
      const [row, col] = resolve(node, context);
      return context.getValue(row, col);
    }
    case 'range': {
      const [rowA, colA] = resolve(node.from, context);
      const [rowB, colB] = resolve(node.to, context);
      const values: FormulaValue[] = [];
      const rowTo = Math.min(Math.max(rowA, rowB), context.rowCount - 1);
      const colTo = Math.min(Math.max(colA, colB), context.columnCount - 1);
      for (let row = Math.max(Math.min(rowA, rowB), 0); row <= rowTo; row++) {
        for (let col = Math.max(Math.min(colA, colB), 0); col <= colTo; col++) {
          if (--context.budget.reads < 0) {
            return new FormulaError('#VALUE!');
          }
          values.push(context.getValue(row, col));
        }
      }

      return values;
    }
    case 'unary': {
      const value = toNumber(evaluate(node.arg, context));
      if (value instanceof FormulaError) {
        return value;
      }
      return node.op === '-' ? -value : value;
    }
    case 'postfix': {
      const value = toNumber(evaluate(node.arg, context));
      return value instanceof FormulaError ? value : value / 100;
    }
    case 'binary': {
      const left = scalar(evaluate(node.left, context));
      const right = scalar(evaluate(node.right, context));
      if (left instanceof FormulaError) {
        return left;
      }
      if (right instanceof FormulaError) {
        return right;
      }
      if (node.op === '&') {
        return toText(left) + toText(right);
      }
      if (['=', '<>', '<', '>', '<=', '>='].includes(node.op)) {
        const result = compare(left, right);
        return {
          '=': result === 0,
          '<>': result !== 0,
          '<': result < 0,
          '>': result > 0,
          '<=': result <= 0,
          '>=': result >= 0,
        }[node.op];
      }
      const a = toNumber(left);
      const b = toNumber(right);
      if (a instanceof FormulaError) {
        return a;
      }
      if (b instanceof FormulaError) {
        return b;
      }
      if (node.op === '+') {
        return a + b;
      }
      if (node.op === '-') {
        return a - b;
      }
      if (node.op === '*') {
        return a * b;
      }
      if (node.op === '/') {
        return b === 0 ? new FormulaError('#DIV/0!') : a / b;
      }
      return a ** b;
    }
    case 'call':
      return callFunction(node.name, node.args, context);
  }
}

function callFunction(name: string, args: Node[], context: FormulaContext): Value {
  const value = (index: number) => evaluate(args[index], context);
  const number = (index: number) => toNumber(value(index));
  const text = (index: number) => toText(value(index));
  const numbers = () =>
    flatten(args.map((arg) => evaluate(arg, context))).filter((item): item is number => typeof item === 'number');

  switch (name) {
    case 'AND':
      return args.every((_, index) => toBool(value(index)));
    case 'OR':
      return args.some((_, index) => toBool(value(index)));
    case 'NOT':
      return !toBool(value(0));
    case 'IF':
      return toBool(value(0)) ? (args[1] ? value(1) : true) : args[2] ? value(2) : false;
    case 'IFERROR': {
      const result = value(0);
      return result instanceof FormulaError ? value(1) : result;
    }
    case 'ROW':
      return args[0]?.type === 'ref' ? resolve(args[0], context)[0] + 1 : context.row + 1;
    case 'COLUMN':
      return args[0]?.type === 'ref' ? resolve(args[0], context)[1] + 1 : context.col + 1;
    case 'MOD': {
      const a = number(0);
      const b = number(1);
      if (a instanceof FormulaError || b instanceof FormulaError || b === 0) {
        return new FormulaError('#DIV/0!');
      }
      return a - b * Math.floor(a / b);
    }
    case 'ABS': {
      const a = number(0);
      return a instanceof FormulaError ? a : Math.abs(a);
    }
    case 'INT': {
      const a = number(0);
      return a instanceof FormulaError ? a : Math.floor(a);
    }
    case 'ROUND': {
      const a = number(0);
      const digits = args[1] ? number(1) : 0;
      if (a instanceof FormulaError || digits instanceof FormulaError) {
        return new FormulaError('#VALUE!');
      }
      return Math.round(a * 10 ** digits) / 10 ** digits;
    }
    case 'ISBLANK': {
      const result = scalar(value(0));
      return result === null || result === '';
    }
    case 'ISNUMBER':
      return typeof scalar(value(0)) === 'number';
    case 'ISTEXT':
      return typeof scalar(value(0)) === 'string';
    case 'ISERROR':
    case 'ISERR':
      return value(0) instanceof FormulaError;
    case 'LEN':
      return text(0).length;
    case 'TRIM':
      return text(0).trim().replace(/\s+/g, ' ');
    case 'UPPER':
      return text(0).toUpperCase();
    case 'LOWER':
      return text(0).toLowerCase();
    case 'LEFT': {
      const count = args[1] ? number(1) : 1;
      return count instanceof FormulaError ? count : text(0).slice(0, count);
    }
    case 'RIGHT': {
      const count = args[1] ? number(1) : 1;
      return count instanceof FormulaError ? count : count ? text(0).slice(-count) : '';
    }
    case 'MID': {
      const start = number(1);
      const count = number(2);
      if (start instanceof FormulaError || count instanceof FormulaError) {
        return new FormulaError('#VALUE!');
      }
      return text(0).substr(start - 1, count);
    }
    case 'EXACT':
      return text(0) === text(1);
    case 'SEARCH':
    case 'FIND': {
      const needle = text(0);
      const haystack = text(1);
      const found = name === 'SEARCH' ? haystack.toLowerCase().indexOf(needle.toLowerCase()) : haystack.indexOf(needle);
      return found < 0 ? new FormulaError('#VALUE!') : found + 1;
    }
    case 'VALUE':
      return number(0);
    case 'SUM':
      return numbers().reduce((sum, item) => sum + item, 0);
    case 'AVERAGE': {
      const items = numbers();
      return items.length ? items.reduce((sum, item) => sum + item, 0) / items.length : new FormulaError('#DIV/0!');
    }
    case 'MIN': {
      const items = numbers();
      return items.length ? Math.min(...items) : 0;
    }
    case 'MAX': {
      const items = numbers();
      return items.length ? Math.max(...items) : 0;
    }
    case 'COUNT':
      return numbers().length;
    case 'COUNTA':
      return flatten(args.map((arg) => evaluate(arg, context))).filter((item) => item !== null && item !== '').length;
    case 'COUNTBLANK':
      return flatten(args.map((arg) => evaluate(arg, context))).filter((item) => item === null || item === '').length;
    case 'COUNTIF': {
      const range = flatten([value(0)]);
      const criteria = scalar(value(1));
      if (criteria instanceof FormulaError) {
        return criteria;
      }
      return range.filter((item) => matchesCriteria(item, criteria)).length;
    }
    case 'TODAY':
      return serialToday(context.date1904);
    case 'NOW':
      return serialToday(context.date1904) + (Date.now() % 86400000) / 86400000;
    case 'YEAR':
    case 'MONTH':
    case 'DAY':
    case 'WEEKDAY': {
      const serial = number(0);
      if (serial instanceof FormulaError) {
        return serial;
      }
      const date = serialToDate(serial, context.date1904);
      if (name === 'YEAR') {
        return date.getUTCFullYear();
      }
      if (name === 'MONTH') {
        return date.getUTCMonth() + 1;
      }
      if (name === 'DAY') {
        return date.getUTCDate();
      }
      return date.getUTCDay() + 1;
    }
    case 'DATE': {
      const year = number(0);
      const month = number(1);
      const day = number(2);
      if (year instanceof FormulaError || month instanceof FormulaError || day instanceof FormulaError) {
        return new FormulaError('#VALUE!');
      }

      const serial = (Date.UTC(year, month - 1, day) - Date.UTC(1899, 11, 30)) / 86400000;
      return context.date1904 ? serial - 1462 : serial;
    }
  }
  return new FormulaError('#NAME?');
}

export function evaluateFormula(node: Node, context: FormulaContext): FormulaValue | undefined {
  const result = scalar(evaluate(node, context));
  return result instanceof FormulaError ? undefined : result;
}

export function isTruthy(value: FormulaValue | undefined): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return false;
}

export function shiftFormula(formula: string, rowDelta: number, colDelta: number): string {
  return formula
    .split(/("(?:[^"]|"")*")/)
    .map((part, index) => {
      if (index % 2 === 1) {
        return part;
      }
      return part.replace(
        /(^|[^A-Za-z0-9_.])(\$?)([A-Z]{1,3})(\$?)(\d+)(?![A-Za-z0-9_(])/g,
        (match, prefix, absCol, letters, absRow, digits) => {
          let col = columnNumber(letters);
          let row = Number(digits) - 1;
          if (!absCol) {
            col += colDelta;
          }
          if (!absRow) {
            row += rowDelta;
          }
          if (col < 0 || row < 0) {
            return match;
          }
          let name = '';
          for (let rest = col; rest >= 0; rest = Math.floor(rest / 26) - 1) {
            name = String.fromCharCode(65 + (rest % 26)) + name;
          }
          return `${prefix}${absCol}${name}${absRow}${row + 1}`;
        },
      );
    })
    .join('');
}
